import { type } from 'ramda';
import { get, set } from 'dot-prop-immutable';
import _get from 'lodash/get';
import _intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import {
  dateOfBirth as dateOfBirthValidator,
  postalCode as postalCodeValidator,
  stakeholderLastName as stakeholderLastNameValidator,
} from '@klarna/mf-validations';
import { formUtils } from 'moet-core';

import { validationRunner, validators } from '@/flow/validation';
import * as personalIdUtils from '@/utils/personalId';
import * as dateUtils from '@/utils/date';

import { STAKEHOLDER_ROLES_V2, STAKEHOLDER_TYPES } from '../constants';

const { APPLICANT, SOLE_TRADER } = STAKEHOLDER_ROLES_V2;

const tPrefixValidation = 'products-fe.error_messages.validation';

const hasAllOfRoles = (s, roles) => roles.find(r => !s.roles.includes(r)) === undefined;
const hasNonOfRoles = (s, roles) => roles.find(r => s.roles.includes(r)) === undefined;
const isIdenticalRoles = ({ roles: stakeholderRoles = [] } = {}, roles = []) => {
  if (stakeholderRoles.length !== roles.length) {
    return false;
  }
  return !roles.find(r => !stakeholderRoles.includes(r));
};
export const roleComparator = {
  hasAllOfRoles,
  hasNonOfRoles,
  isIdenticalRoles,
};

/**
 * Init Stakeholder form data
 * I.e., add initial "isValid" flags etc.
 *
 * @param {Object} stakeholder
 * @param {Object} state
 *
 * @return {Object} Initialized stakeholder
 */
const initStakeholderFormData = (stakeholderFormData, state = {}) => {
  const { address, ...rest } = stakeholderFormData;
  const initAddress = formUtils.initForm(address, state);
  const initRest = formUtils.initForm(rest, state);

  return Object.keys(stakeholderFormData).reduce(
    (acc, key) => ({ ...acc, [key]: key === 'address' ? initAddress : initRest[key] }),
    {},
  );
};

/**
 * Get required field validators
 *
 * @param {String} errorMessageKey
 *
 * @returns {Array} validators
 */
const getRequiredFieldValidators = (errorMessageKey, skipField = false) => {
  return [
    {
      callback: val => (skipField ? true : validators.common.isRequired(val)),
      errorMessage: `${tPrefixValidation}.${errorMessageKey}`,
    },
  ];
};

/**
 * Check if a role is enabled
 *
 * @param {Array} rolesOfCurrentStakeholder
 * @param {Number} countOfStakeholdersWithRole
 * @param {Object} role
 * @param {Number} max
 */
export const isRoleEnabled = (
  rolesOfCurrentStakeholder,
  countOfStakeholdersWithRole,
  role,
  max,
) => {
  return countOfStakeholdersWithRole < max || rolesOfCurrentStakeholder.includes(role);
};

/**
 * Get a list of roles that cannot be added to a stakeholder.
 * See the tests for examples.
 *
 * @param {Object} roleConstraints - object with roles as keys, and an object with constraints
 *                                   for that role. Currently, only `max` is checked.
 * @param {Array} allStakeholders  - each stakeholder should have property 'roles'
 * @param {Array} currentRoles     - roles that are already added to the current stakeholder
 */
export const getDisabledRoles = (roleConstraints, allStakeholders, currentRoles) => {
  const disabledRoles = [];

  Object.keys(roleConstraints).forEach(r => {
    if (
      !isRoleEnabled(
        currentRoles,
        allStakeholders.filter(s => s.roles && s.roles.includes(r)).length,
        r,
        roleConstraints[r].max,
      )
    ) {
      disabledRoles.push(r);
    }
  });

  return disabledRoles;
};

/**
 * On blur stakeholder field
 *
 * @param {Number} index
 * @param {String} key
 * @param {Object} state
 * @param {(newStakeholders: []) => void} callbackFunction
 */
export const onBlurStakeholderDataField = (index, key, state, callbackFunction) => {
  const dataKey = `data.${key}`;
  const { stakeholders } = state;
  const currentField = get(stakeholders[index], dataKey);
  const countryValue = get(stakeholders[index], 'data.address.country.value');
  const dateOfBirthField = get(stakeholders[index], 'data.dateOfBirth');
  const merchantCountry = _get(state, 'country');
  const merchantEntityType = _get(state, 'entityType');
  const appRegion = _get(state, 'region');
  const { canDeriveDateOfBirth = false } = personalIdUtils.getField(
    appRegion,
    merchantCountry,
    countryValue,
    merchantEntityType,
  );

  // prefill DOB for nordics if the personal id was provided
  if (
    key === 'personalId' &&
    !isEmpty(currentField.value) &&
    !/[ ]{1,}/.test(currentField.value) &&
    canDeriveDateOfBirth
  ) {
    const { isValid } = validationRunner(
      dateUtils.getDateOfBirthFromPersonalId(countryValue, currentField.value),
      dateOfBirthField.validators,
      {
        state,
      },
    );

    dateOfBirthField.value = dateUtils.getDateOfBirthFromPersonalId(
      countryValue,
      currentField.value,
    );
    dateOfBirthField.isInitialized = true;
    dateOfBirthField.isValid = isValid;
  }

  return onChangeStakeholderDataField(index, key, currentField.value, state, callbackFunction);
};

/**
 * On change stakeholder field
 *
 * @param {Number} index
 * @param {String} key
 * @param {Any} value
 * @param {Object} state
 * @param {(newStakeholders: []) => void} callbackFunction
 */
export const onChangeStakeholderDataField = (index, key, value, state, callbackFunction) => {
  const { stakeholders } = state;
  const mainFieldDataKey = `data.${key}`;
  const mainField = get(stakeholders[index], mainFieldDataKey);

  const dataKeys = [
    mainFieldDataKey,
    ...(mainField.resetAndValidateFields || []).map(k => `data.${k}`),
  ];

  const editedStakeholder = dataKeys.reduce((underEditStakeholder, dataKey) => {
    const currentField = get(stakeholders[index], dataKey);
    const currentValue =
      mainFieldDataKey === dataKey ? value : dataKey === 'data.isPersonalIdPristine' ? false : '';

    const { isValid, errorMessage } = validationRunner(currentValue, currentField.validators, {
      state: underEditStakeholder.data,
    });

    return set(underEditStakeholder, dataKey, {
      ...currentField,
      isValid,
      value: currentValue,
      isInitialized: true,
      errorMessageTKey: errorMessage,
    });
  }, stakeholders[index]);

  const newStakeholders = [
    ...stakeholders.slice(0, index),
    editedStakeholder,
    ...stakeholders.slice(index + 1),
  ];
  if (type(callbackFunction) === 'Function') {
    callbackFunction(newStakeholders);
  }

  return newStakeholders;
};

/**
 * Get an array of the stakeholders with the minimum reqiured number of the stakeholders.
 *
 * @param {Nubmer} minNumber - The min number of stakeholders to return
 * @param {String} type - The type of stakeholder to default to
 * @param {Array} roles - roles that will be added to the new stakeholder
 * @param {Array} prefilledStakeholders - Prefill (overwrites type and roles if data is provided)
 * @param {String/Boolean} forceCountry - Force this country value on the stakeholder
 */
export const getDefaultStakeholders = (
  min,
  type,
  roles,
  prefilledStakeholders = [],
  state = {},
  forceCountry = false,
  stakeholderLength,
) => {
  const arr = prefilledStakeholders.map(
    ({ data, type: stakeholderType = type, roles: stakeholderRoles = roles, ...s }) =>
      getDefaultStakeholder(
        stakeholderType,
        stakeholderRoles,
        {
          ...s,
          ...data,
        },
        state,
        {
          forceCountry,
        },
      ),
  );
  for (let index = stakeholderLength; index < min; index++) {
    arr.push(
      getDefaultStakeholder(type, roles, undefined, state, {
        forceCountry,
      }),
    );
  }
  return arr;
};

/**
 * Get stakeholder of PERSON type
 *
 * @param {Array} defaultRoles Roles
 * @param {Object} stakeholder To prefill with (will overwrite roles if data is provided)
 * @param {String/Boolean} forceCountry - Force this country value on the stakeholder
 *
 * @returns {Object} stakeholder
 */
const getPersonStakeholder = (
  defaultRoles = [],
  stakeholder = {},
  state = {},
  { forceCountry = false } = {},
) => {
  const {
    reactFormId = stakeholder.id || `${Math.random()}`,
    roles = defaultRoles,
    firstName = '',
    lastName = '',
    dateOfBirth = '',
    personalId = '',
    address = {},
  } = stakeholder;
  const { street = '', postalCode = '', city = '', country = '', region = '' } = address;
  const merchantCountry = _get(state, 'country');
  const appRegion = _get(state, 'region');
  const merchantEntityType = _get(state, 'entityType');

  return {
    id: stakeholder.id || stakeholder.resourceId,
    resourceId: stakeholder.resourceId,
    reactFormId, // used to generate unique key for rendering in a list
    type: STAKEHOLDER_TYPES.PERSON,
    hasPersonalId: stakeholder.hasPersonalId,
    roles,
    data: initStakeholderFormData({
      isPersonalIdPristine: {
        // Internal control
        isInitialized: true,
        value: true, // Initialized with pristine
        isValid: true,
      },
      firstName: {
        validators: getRequiredFieldValidators('given_name'),
        isInitialized: false,
        value: firstName,
        isValid: false,
        errorMessageTKey: '',
      },
      lastName: {
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefixValidation}.family_name.general`,
          },
          {
            callback: stakeholderLastNameValidator.validate,
            errorMessage: `${tPrefixValidation}.family_name.too_short`,
          },
        ],
        isInitialized: false,
        value: lastName,
        isValid: false,
        errorMessageTKey: '',
      },
      address: {
        country: forceCountry
          ? {
              isInitialized: true,
              isValid: true,
              value: forceCountry,
              extraProps: {
                disabled: true,
              },
            }
          : {
              validators: getRequiredFieldValidators('country'),
              isInitialized: false,
              value: country || merchantCountry,
              isValid: false,
              errorMessageTKey: '',
              resetAndValidateFields: ['address.region', 'personalId', 'isPersonalIdPristine'],
            },
        postalCode: {
          validators: [
            {
              callback: (val, { country }) => postalCodeValidator.validate(val, { country }),
              errorMessage: `${tPrefixValidation}.postal_code.error`,
              stateSelector: state => ({ country: get(state, 'address.country.value') }),
            },
          ],
          isInitialized: false,
          value: postalCode,
          isValid: false,
          errorMessageTKey: '',
        },
        street: {
          validators: getRequiredFieldValidators('street_address'),
          isInitialized: false,
          value: street,
          isValid: false,
          errorMessageTKey: '',
        },
        city: {
          validators: getRequiredFieldValidators('city'),
          isInitialized: false,
          value: city,
          isValid: false,
          errorMessageTKey: '',
        },
        region: {
          validators: [
            {
              callback: (val, stateFromSelector) => {
                const { country, isInitialized } = stateFromSelector;
                return country === 'US' && isInitialized ? !!val : true;
              },
              errorMessage: ({ country }) =>
                `${tPrefixValidation}.${country === 'US' ? 'state' : 'region'}`,
              stateSelector: state => ({
                country: get(state, 'address.country.value'),
                isInitialized: get(state, 'address.region.isInitialized'),
              }),
            },
          ],
          isInitialized: merchantCountry !== 'US',
          value: region,
        },
      },
      dateOfBirth: {
        validators: [
          // TODO: change to stakeholderDateOfBirth when it's able to return error message keys
          {
            callback: value => dateOfBirthValidator.validate(value, { maxAge: 125 }),
            errorMessage: `${tPrefixValidation}.date_of_birth.general`,
          },
          _intersection(defaultRoles, [APPLICANT, SOLE_TRADER]).length
            ? {
                callback: value =>
                  dateOfBirthValidator.validate(value, { minAge: 18, maxAge: 125 }),
                errorMessage: `${tPrefixValidation}.date_of_birth.too_young_eighteen`,
              }
            : {
                callback: value =>
                  dateOfBirthValidator.validate(value, { minAge: 16, maxAge: 125 }),
                errorMessage: `${tPrefixValidation}.date_of_birth.too_young`,
              },
        ],
        isInitialized: false,
        // if not null, the bubble-ui date field triggers onInvalidDate on component mount
        value: dateOfBirth || null,
        isValid: false,
        errorMessageTKey: `${tPrefixValidation}.date_of_birth.general`,
      },
      personalId: {
        validators: [
          {
            callback: (
              val,
              { country, hasPersonalId = stakeholder.hasPersonalId, isPersonalIdPristine },
            ) => {
              const isPersonalIdRequired = personalIdUtils.isRequired({
                region: appRegion,
                merchantCountry,
                stakeholderCountry: country,
              });

              if (isPersonalIdRequired) {
                // If it's required
                if (!!val || (!val && isPersonalIdPristine !== false && hasPersonalId)) {
                  // Valid if field is there or if field is persisted and pristine on the form
                  return true;
                } else {
                  // Invalid otherwise
                  return false;
                }
              } else {
                // If it's not required, always valid
                return true;
              }
            },
            errorMessage: ({ country }) => {
              const { translationKey } = personalIdUtils.getField(
                appRegion,
                merchantCountry,
                country,
                merchantEntityType,
              );

              return `${tPrefixValidation}.personal_id.${translationKey}.general`;
            },
            stateSelector: state => ({
              country: get(state, 'address.country.value'),
              hasPersonalId: get(state, 'hasPersonalId'),
              isPersonalIdPristine: get(state, 'isPersonalIdPristine.value'),
            }),
          },
          {
            callback: (
              val,
              { country, isPersonalIdPristine, hasPersonalId = stakeholder.hasPersonalId, ...rest },
            ) => {
              const { formatValidator } = personalIdUtils.getField(
                appRegion,
                merchantCountry,
                country,
                merchantEntityType,
              );

              // If the personal id hasn't been touched or field has no format validator
              // return valid
              if (
                !formatValidator || // If there is no format validator
                (isPersonalIdPristine !== false && hasPersonalId) // If it's pristine
              ) {
                return true;
              } else {
                return !formatValidator(val).error;
              }
            },
            errorMessage: ({ country }) => {
              const { translationKey } = personalIdUtils.getField(
                appRegion,
                merchantCountry,
                country,
                merchantEntityType,
              );

              return `${tPrefixValidation}.personal_id.${translationKey}.format`;
            },
            stateSelector: state => ({
              country: get(state, 'address.country.value'),
              isPersonalIdPristine: get(state, 'isPersonalIdPristine.value'),
              hasPersonalId: get(state, 'hasPersonalId'),
            }),
          },
        ],
        isInitialized:
          !personalIdUtils.isRequired({
            region: appRegion,
            merchantCountry,
          }) || stakeholder.hasPersonalId,
        isValid:
          !personalIdUtils.isRequired({
            region: appRegion,
            merchantCountry,
          }) || stakeholder.hasPersonalId,
        value: personalId,
      },
    }),
  };
};

/**
 * Get a default stakeholder.
 *
 * @param {String} type The stakeholder type COMPANY|PERSON
 * @param {Array} defaultRoles Roles that will be added to the new stakeholder
 * @param {Object} stakeholder To prefill with (will overwrite type and roles if data is provided)
 * @param {Object} state React state
 * @param {String|Boolean} forceCountry - Force this country value on the stakeholder
 *
 * @returns {Object} stakeholder
 */
export const getDefaultStakeholder = (
  type,
  defaultRoles,
  stakeholder = {},
  state = {},
  { forceCountry = false } = {},
) => {
  switch (type) {
    case STAKEHOLDER_TYPES.PERSON:
      return getPersonStakeholder(defaultRoles, stakeholder, state, {
        forceCountry,
      });
    case STAKEHOLDER_TYPES.COMPANY:
      throw new Error('Not implemented');
    default:
      throw new Error(`Unknown stakeholder type: ${type}`);
  }
};

/**
 * Remove stakeholder by index
 *
 * @param {Number} index
 * @param {Array} stakeholders
 * @param {(newStakeholders: []) => void} callbackFunction
 * @param {Number} minNumber
 */
export const removeStakeholderByIndex = (index, stakeholders, callbackFunction, min = 0) => {
  if (min >= stakeholders.length) {
    return;
  }
  callbackFunction([...stakeholders.slice(0, index), ...stakeholders.slice(index + 1)]);
};

/**
 * Get stakeholders for validation
 *
 * @param {Array} stakeholders
 */
const getStakeholdersDataForValidation = stakeholders => {
  return stakeholders.map(({ data }) => data);
};

/**
 * Get stakeholders mapped for requests
 *
 * @param {Array} stakeholders
 *
 * @returns {Array}
 */
export const getStakeholdersMappedValues = stakeholders => {
  return stakeholders.map(({ id, resourceId, type, roles, data }) => {
    const { address, ...basicFields } = data;
    const { country: { value: countryKey } = {} } = address || {};
    const { isPersonalIdPristine, ...mappedDataFields } = formUtils.getMappedValues(
      basicFields,
      countryKey,
    );
    const stakeholder = {
      id,
      resourceId,
      type,
      roles,
      data: {
        ...mappedDataFields,
        address: formUtils.getMappedValues(address),
        ...(isPersonalIdPristine ? { personalId: undefined } : {}),
      },
    };
    return stakeholder;
  });
};

/**
 * Check if all stakeholders are valid
 *
 * @param {Array} stakeholders
 *
 * @returns {Boolean}
 */
export const isStakeholdersValid = stakeholders => {
  const stakeholdersForValidation = getStakeholdersDataForValidation(stakeholders);
  return stakeholdersForValidation.every(formUtils.isFormInitializedAndValid);
};

/**
 * Checks if stakeholder role constraints and min/max are valid
 *
 * @param {Array} stakeholders
 * @param {Object} constraints
 *
 * @returns {Boolean}
 */
export const isStakeholderConstraintsValid = (
  stakeholders,
  { min = 0, max = Number.MAX_SAFE_INTEGER, roleConstraints = {} } = {},
) => {
  if (min > stakeholders.length) {
    return false;
  }
  if (max < stakeholders.length) {
    return false;
  }

  const violatedRoleConstraint = Object.keys(roleConstraints).find(
    role =>
      stakeholders.filter(({ roles = [] }) => roles.includes(role)).length >
      roleConstraints[role].max,
  );

  if (violatedRoleConstraint) {
    return false;
  }

  return true;
};

/**
 * Get stakeholders based on roles
 *
 * @param {Array} stakeholders
 * @param {Array} mustHaveRoles
 * @param {Array} cantHaveRoles
 *
 * @returns {Array}
 */
export const getStakeholdersBasedOnRoles = (
  stakeholders = [],
  mustHaveRoles = [],
  cantHaveRoles = [],
) => stakeholders.filter(s => hasAllOfRoles(s, mustHaveRoles) && hasNonOfRoles(s, cantHaveRoles));

export const inversedGetStakeholdersBasedOnRoles = (
  stakeholders = [],
  mustHaveRoles = [],
  cantHaveRoles = [],
) => {
  return stakeholders.filter(
    s => hasNonOfRoles(s, mustHaveRoles) || !hasNonOfRoles(s, cantHaveRoles),
  );
};

/**
 * Count the number of stakeholders for each role
 *
 * @param {Array} stakeholders
 * @param {Array} roles
 *
 * @returns {Object} role => count
 */
export const countStakeholdersPerRole = (stakeholders = [], roles = []) => {
  const countPerRole = {};
  roles.forEach(r => {
    countPerRole[r] = stakeholders.filter(({ roles: sRoles = [] }) => sRoles.includes(r)).length;
  });
  return countPerRole;
};

/**
 * Update role constraints based on a list of already added stakeholders
 *
 * @param {Array} stakeholders
 * @param {Object} roleConstraints
 *
 * @returns {Object} new role constraints
 */
export const updateRoleConstraintsBasedOnStakeholders = (
  stakeholders = [],
  roleConstraints = {},
) => {
  const countPerRole = countStakeholdersPerRole(stakeholders, Object.keys(roleConstraints));
  const modifiedRoleConstraints = {};
  Object.keys(roleConstraints).forEach(role => {
    const { min = 0, max = Number.MAX_SAFE_INTEGER } = roleConstraints[role] || {};
    const count = countPerRole[role];
    modifiedRoleConstraints[role] = {
      max: max !== null ? max - count : null,
      min: Math.max(min - count, 0),
    };
  });
  return modifiedRoleConstraints;
};
